import type { FC, ReactNode } from 'react'
import { useEffect, useRef, useState } from 'react'

import type { SxProps, Theme } from '@mui/material'
import { Box, Typography } from '@mui/material'
import { Check, East, RadioButtonUnchecked } from '@mui/icons-material'

import { HOME_CONTENT } from '../defaults'

export const LiveActivityDemoSection: FC = () => {
  const { stepIndex, step, previousStep, isTransitioning } = useDemoPlayback(DEMO_SCRIPT)

  return (
    <Box component="section" sx={sectionContainerSx}>
      <Typography component="h2" sx={titleSx}>
        {HOME_CONTENT.liveActivity.title}
      </Typography>
      <Typography sx={subtitleSx}>{HOME_CONTENT.liveActivity.description}</Typography>

      <Box
        sx={demoContainerSx}
        role="status"
        aria-live="polite"
        aria-label="Live activity demonstration"
      >
        {previousStep !== null && isTransitioning && (
          <Box sx={slideOutSx}>{renderStep(previousStep, stepIndex - 1)}</Box>
        )}
        <Box sx={isTransitioning ? slideInSx : staticSx}>{renderStep(step, stepIndex)}</Box>
      </Box>
    </Box>
  )
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type DemoStepStatus = 'pre_tool' | 'post_tool' | 'system' | 'idle'

interface TodoConfig {
  readonly pending?: number
  readonly inProgress?: number
  readonly completed?: number
}

interface DemoStep {
  readonly status: DemoStepStatus
  readonly durationMs: number
  readonly verb?: string
  readonly context?: string
  readonly suffix?: string
  readonly pulseDot?: boolean
  readonly todo?: TodoConfig
}

// ---------------------------------------------------------------------------
// Demo Script
// ---------------------------------------------------------------------------

const DEMO_SCRIPT: readonly DemoStep[] = [
  // Phase: Session start + plan
  { status: 'system', durationMs: 2500, context: 'Session started', pulseDot: true },
  {
    status: 'post_tool',
    durationMs: 1800,
    verb: 'Tracked',
    context: 'agent todos',
    todo: { pending: 3 },
  },

  // Phase: Discovery
  { status: 'pre_tool', durationMs: 1500, verb: 'Globbing', context: 'src/features/**/*.tsx' },
  { status: 'post_tool', durationMs: 1200, verb: 'Globbed', context: 'src/features/**/*.tsx' },
  {
    status: 'pre_tool',
    durationMs: 1500,
    verb: 'Reading',
    context: 'src/features/auth/LoginForm.tsx',
  },
  {
    status: 'post_tool',
    durationMs: 1200,
    verb: 'Read',
    context: 'src/features/auth/LoginForm.tsx',
  },
  { status: 'pre_tool', durationMs: 1500, verb: 'Grepping', context: 'validateEmail' },
  { status: 'post_tool', durationMs: 1200, verb: 'Grepped', context: 'validateEmail' },

  // Phase: Task 1 — Research
  {
    status: 'post_tool',
    durationMs: 1500,
    verb: 'Tracked',
    context: 'agent todos',
    todo: { pending: 2, inProgress: 1 },
  },
  { status: 'pre_tool', durationMs: 1500, verb: 'Calling', context: 'context7 resolve-library-id' },
  { status: 'post_tool', durationMs: 1200, verb: 'Called', context: 'context7 resolve-library-id' },
  {
    status: 'pre_tool',
    durationMs: 1500,
    verb: 'Searching',
    context: 'React form validation best practices',
  },
  {
    status: 'post_tool',
    durationMs: 1200,
    verb: 'Searched',
    context: 'React form validation best practices',
  },
  {
    status: 'pre_tool',
    durationMs: 1500,
    verb: 'Fetching',
    context: 'https://react-hook-form.com/api',
  },
  {
    status: 'post_tool',
    durationMs: 1200,
    verb: 'Fetched',
    context: 'https://react-hook-form.com/api',
  },
  {
    status: 'post_tool',
    durationMs: 1500,
    verb: 'Tracked',
    context: 'agent todos',
    todo: { pending: 2, completed: 1 },
  },

  // Phase: Task 2 — Implementation
  {
    status: 'post_tool',
    durationMs: 1500,
    verb: 'Tracked',
    context: 'agent todos',
    todo: { pending: 1, inProgress: 1, completed: 1 },
  },
  {
    status: 'pre_tool',
    durationMs: 1500,
    verb: 'Writing',
    context: 'src/features/auth/validators.ts',
  },
  {
    status: 'post_tool',
    durationMs: 1200,
    verb: 'Wrote',
    context: 'src/features/auth/validators.ts',
    suffix: '+28',
  },
  {
    status: 'pre_tool',
    durationMs: 1500,
    verb: 'Editing',
    context: 'src/features/auth/LoginForm.tsx',
  },
  {
    status: 'post_tool',
    durationMs: 1200,
    verb: 'Edited',
    context: 'src/features/auth/LoginForm.tsx',
    suffix: '+12 -3',
  },
  {
    status: 'post_tool',
    durationMs: 1500,
    verb: 'Tracked',
    context: 'agent todos',
    todo: { pending: 1, completed: 2 },
  },

  // Phase: Task 3 — Testing
  {
    status: 'post_tool',
    durationMs: 1500,
    verb: 'Tracked',
    context: 'agent todos',
    todo: { inProgress: 1, completed: 2 },
  },
  { status: 'pre_tool', durationMs: 1500, verb: 'Bashing', context: 'npm test -- auth' },
  { status: 'post_tool', durationMs: 1200, verb: 'Bashed', context: 'npm test -- auth' },
  {
    status: 'post_tool',
    durationMs: 1800,
    verb: 'Tracked',
    context: 'agent todos',
    todo: { completed: 3 },
  },

  // Phase: Wind down
  { status: 'system', durationMs: 2000, context: 'Compacting conversation', pulseDot: true },
  { status: 'system', durationMs: 2500, context: 'Claude is waiting for your input' },
  { status: 'idle', durationMs: 2000, context: 'Awaiting activity' },
]

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

interface UseDemoPlaybackReturn {
  readonly stepIndex: number
  readonly step: DemoStep
  readonly previousStep: DemoStep | null
  readonly isTransitioning: boolean
}

const FALLBACK_STEP: DemoStep = { status: 'idle', durationMs: 2000, context: 'Awaiting activity' }

const TRANSITION_DURATION_MS = 300

const useDemoPlayback = (script: readonly DemoStep[]): UseDemoPlaybackReturn => {
  const [stepIndex, setStepIndex] = useState(0)
  const [previousStepIndex, setPreviousStepIndex] = useState<number | null>(null)
  const [isTransitioning, setIsTransitioning] = useState(false)

  const prefersReducedMotion = useRef(
    'matchMedia' in globalThis && globalThis.matchMedia('(prefers-reduced-motion: reduce)').matches,
  )

  const currentStep: DemoStep = script[stepIndex] ?? FALLBACK_STEP

  useEffect(() => {
    if (prefersReducedMotion.current) return

    const advanceId = setTimeout(() => {
      setPreviousStepIndex(stepIndex)
      setIsTransitioning(true)
      setStepIndex((prev) => (prev + 1) % script.length)
    }, currentStep.durationMs)

    return () => clearTimeout(advanceId)
  }, [stepIndex, script, currentStep.durationMs])

  useEffect(() => {
    if (!isTransitioning) return

    const clearId = setTimeout(() => {
      setPreviousStepIndex(null)
      setIsTransitioning(false)
    }, TRANSITION_DURATION_MS)

    return () => clearTimeout(clearId)
  }, [isTransitioning])

  const previousStep = previousStepIndex === null ? null : (script[previousStepIndex] ?? null)

  return { stepIndex, step: currentStep, previousStep, isTransitioning }
}

// ---------------------------------------------------------------------------
// Rendering
// ---------------------------------------------------------------------------

const renderStep = (step: DemoStep, index: number): ReactNode => {
  if (step.status === 'idle') {
    return (
      <Box key={index} sx={stepContentSx}>
        <Box component="span" sx={dotSx('grey.500', false)} />
        <Typography component="span" sx={idleTextSx}>
          {step.context}
        </Typography>
      </Box>
    )
  }

  if (step.status === 'system') {
    return (
      <Box key={index} sx={stepContentSx}>
        <Box component="span" sx={dotSx('#00bcd4', step.pulseDot === true)} />
        <Typography component="span" sx={systemTextSx}>
          {step.context}
        </Typography>
      </Box>
    )
  }

  const dotColor = step.status === 'pre_tool' ? 'primary.main' : 'success.main'

  return (
    <Box key={index} sx={stepContentSx}>
      <Box component="span" sx={dotSx(dotColor, false)} />
      <Typography component="span" sx={verbSx}>
        {step.verb}
      </Typography>
      <Typography component="span" sx={contextSx}>
        {step.context}
      </Typography>
      {step.todo !== undefined && <TodoStats {...step.todo} />}
      {step.todo === undefined && step.suffix !== undefined && <DiffStats suffix={step.suffix} />}
    </Box>
  )
}

const DiffStats: FC<{ readonly suffix: string }> = ({ suffix }) => {
  const parts = suffix.split(' ')

  return (
    <Box component="span" sx={statsSx}>
      {parts.map((part) => {
        let sx = neutralStatSx
        if (part.startsWith('+')) sx = additionSx
        else if (part.startsWith('-')) sx = removalSx
        return (
          <Typography key={part} component="span" sx={sx}>
            {part}
          </Typography>
        )
      })}
    </Box>
  )
}

interface TodoStatsProps {
  readonly pending?: number
  readonly inProgress?: number
  readonly completed?: number
}

const TodoStats: FC<TodoStatsProps> = ({ pending, inProgress, completed }) => (
  <Box component="span" sx={todoContainerSx}>
    {pending !== undefined && pending > 0 && (
      <Box component="span" sx={todoItemSx}>
        <RadioButtonUnchecked sx={pendingIconSx} />
        <Typography component="span" sx={pendingCountSx}>
          ({pending})
        </Typography>
      </Box>
    )}
    {inProgress !== undefined && inProgress > 0 && (
      <Box component="span" sx={todoItemSx}>
        <East sx={inProgressIconSx} />
        <Typography component="span" sx={inProgressCountSx}>
          ({inProgress})
        </Typography>
      </Box>
    )}
    {completed !== undefined && completed > 0 && (
      <Box component="span" sx={todoItemSx}>
        <Check sx={completedIconSx} />
        <Typography component="span" sx={completedCountSx}>
          ({completed})
        </Typography>
      </Box>
    )}
  </Box>
)

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const sectionContainerSx: SxProps<Theme> = {
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  py: { xs: 4, md: 6 },
  px: { xs: 2, md: 4 },
}

const titleSx: SxProps<Theme> = {
  color: 'text.primary',
  fontSize: { xs: '1.5rem', md: '1.75rem' },
  fontWeight: 600,
  textAlign: 'center',
  mb: 1,
}

const subtitleSx: SxProps<Theme> = {
  color: 'text.secondary',
  fontSize: { xs: '1rem', md: '1.1rem' },
  fontWeight: 400,
  textAlign: 'center',
  mb: { xs: 4, md: 6 },
}

const demoContainerSx: SxProps<Theme> = (theme) => ({
  width: '100%',
  maxWidth: 720,
  px: { xs: 2, md: 3 },
  py: 2.5,
  borderRadius: 2,
  border:
    theme.palette.mode === 'dark'
      ? '1px solid rgba(255, 255, 255, 0.06)'
      : '1px solid rgba(62, 39, 35, 0.08)',
  backgroundColor:
    theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.03)' : 'rgba(62, 39, 35, 0.03)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: 40,
  position: 'relative',
  overflow: 'hidden',
})

const stepContentSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  gap: 1.5,
}

const staticSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  gap: 1.5,
}

const slideInSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  gap: 1.5,
  '@media (prefers-reduced-motion: no-preference)': {
    '@keyframes slideIn': {
      from: { opacity: 0, transform: 'translateX(20px)' },
      to: { opacity: 1, transform: 'translateX(0)' },
    },
    animation: 'slideIn 300ms ease-out both',
  },
}

const slideOutSx: SxProps<Theme> = {
  position: 'absolute',
  display: 'flex',
  alignItems: 'center',
  gap: 1.5,
  '@media (prefers-reduced-motion: no-preference)': {
    '@keyframes slideOut': {
      from: { opacity: 1, transform: 'translateX(0)' },
      to: { opacity: 0, transform: 'translateX(-20px)' },
    },
    animation: 'slideOut 300ms ease-out both',
  },
}

const dotSx = (color: string, pulse: boolean): SxProps<Theme> => ({
  width: 8,
  height: 8,
  borderRadius: '50%',
  backgroundColor: color,
  flexShrink: 0,
  ...(pulse
    ? {
        '@media (prefers-reduced-motion: no-preference)': {
          '@keyframes dotPulse': {
            '0%, 100%': { opacity: 1, transform: 'scale(1)' },
            '50%': { opacity: 0.6, transform: 'scale(1.2)' },
          },
          animation: 'dotPulse 1.5s ease-in-out infinite',
        },
      }
    : {}),
})

const verbSx: SxProps<Theme> = {
  fontSize: '0.75rem',
  color: 'text.primary',
  fontStyle: 'italic',
  flexShrink: 0,
}

const contextSx: SxProps<Theme> = {
  fontSize: '0.75rem',
  color: 'error.light',
  fontWeight: 500,
  fontStyle: 'italic',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}

const systemTextSx: SxProps<Theme> = {
  fontSize: '0.75rem',
  color: 'text.secondary',
  fontStyle: 'italic',
}

const idleTextSx: SxProps<Theme> = {
  fontSize: '0.8125rem',
  color: 'text.disabled',
  fontStyle: 'italic',
}

const statsSx: SxProps<Theme> = {
  display: 'inline-flex',
  gap: 0.5,
  flexShrink: 0,
}

const additionSx: SxProps<Theme> = {
  fontSize: '0.75rem',
  fontStyle: 'italic',
  color: 'success.main',
}

const removalSx: SxProps<Theme> = {
  fontSize: '0.75rem',
  fontStyle: 'italic',
  color: 'error.main',
}

const neutralStatSx: SxProps<Theme> = {
  fontSize: '0.75rem',
  fontStyle: 'italic',
  color: 'text.secondary',
}

const todoContainerSx: SxProps<Theme> = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 0.75,
  lineHeight: 1,
}

const todoItemSx: SxProps<Theme> = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 0.25,
  lineHeight: 1,
}

const pendingIconSx: SxProps<Theme> = { fontSize: 10, color: 'warning.light' }
const inProgressIconSx: SxProps<Theme> = { fontSize: 14, color: 'info.main' }
const completedIconSx: SxProps<Theme> = { fontSize: 14, color: 'success.main' }

const pendingCountSx: SxProps<Theme> = {
  fontSize: '0.75rem',
  fontWeight: 500,
  fontStyle: 'italic',
  color: 'warning.light',
}

const inProgressCountSx: SxProps<Theme> = {
  fontSize: '0.75rem',
  fontWeight: 500,
  fontStyle: 'italic',
  color: 'info.main',
}

const completedCountSx: SxProps<Theme> = {
  fontSize: '0.75rem',
  fontWeight: 500,
  fontStyle: 'italic',
  color: 'success.main',
}
